import React, { useLayoutEffect } from 'react';
import { ErrorBoundary } from '@mentimeter/errors/ErrorBoundary';
import type { QuestionWithSlide } from '@mentimeter/voting-schema/api-types-overrides';
import { Box } from '@mentimeter/ragnar-ui/box';
import { getModuleById } from '../../../slide-types/getModuleById';
import type { ModuleComponentProps } from '../../../slide-types/types';
import { ModuleError } from './ModuleError';

interface ModuleProps {
  question: QuestionWithSlide;
  children: (
    Content: React.ComponentType<ModuleComponentProps>,
    Interactive: React.ComponentType<ModuleComponentProps>,
  ) => React.ReactNode;
}

/**
 * Returns true if any element in the  bottom panel have focus.
 * Bottom panel hosts CFA and QFA, and having focus there indicates that the
 * user is currently interacting with these features and should not be forced away.
 */
const isFocusWithinBottomPanel = () => {
  return document.querySelector('.bottom-panel :focus') !== null;
};

export const Module = ({ question, children }: ModuleProps) => {
  const { Interactive, Content } = getModuleById(question.module_id);

  useLayoutEffect(() => {
    if (isFocusWithinBottomPanel()) return;

    // A11y: Focus on main when slide is changed
    document.querySelector('main')?.focus();
  }, [question.slidePublicKey]);

  return (
    <ErrorBoundary feature="slides" fallback={<ModuleError />}>
      <Box width="100%">{children(Content, Interactive)}</Box>
    </ErrorBoundary>
  );
};
